
import type { PropType } from 'vue'
import type { FieldValuesComposableLoadParams } from '../../composables/use-field-values'
import type {
  ErrorMessage,
  FieldValue,
  RegularFilter,
  RegularFilterSelectedValues,
} from '../../models'
import type { EpbSelectOption } from './epb-select'
import type { FieldValueSelectMessages, FieldValueSelectValue } from './field-value-select.types'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { useApiError } from '../../composables/use-api-error'
import { getFieldValuesLoadParamsKey, useFieldValues } from '../../composables/use-field-values'
import { ArrayUtils } from '../../utils/array.utils'
import { FiltersUtils } from '../../utils/filters.utils'
import FiltersAside from '../filters/filters-aside.component.vue'
import { EpbSelect } from './epb-select'
import { EpbSwitch } from './epb-switch'
import { EpbTextbox } from './epb-textbox'

export default defineComponent({
  name: 'FieldValueSelect',
  components: {
    EpbSelect,
    EpbSwitch,
    EpbTextbox,
    FiltersAside,
  },
  props: {
    value: {
      type: Object as PropType<FieldValueSelectValue>,
      required: true,
    },
    nameOptions: {
      type: Array as PropType<Array<EpbSelectOption>>,
      required: true,
    },
    pivots: {
      type: Object as PropType<Record<string, RegularFilter>>,
      default: () => ({}),
    },
    messages: {
      type: Object as PropType<FieldValueSelectMessages>,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: undefined,
    },
    position: {
      type: String as PropType<'column' | 'row'>,
      default: 'column',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isExplain: {
      type: Boolean,
      default: false,
    },
    allowCustomValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    input: (value: FieldValueSelectValue) => {},
    error: (message: ErrorMessage) => {},
  },
  setup(props, { emit }) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const { fieldValues, loadFieldValues } = useFieldValues()

    const { getErrorMessage } = useApiError()

    const baseClass = 'epb-field-value-select'

    const loadingValues = ref(false)

    let lastLoadedParamsKey: string | undefined

    const filtersSelectedValues = ref<Record<string, RegularFilterSelectedValues>>({})

    const valueOptions = computed<Array<EpbSelectOption>>(() => {
      let result: Array<EpbSelectOption>
      if (props.value.name) {
        function map(fieldValue: FieldValue, level = 0): EpbSelectOption {
          const label = fieldValue.name || fieldValue.externalId
          return {
            value: fieldValue.id,
            label,
            help: label !== fieldValue.externalId ? fieldValue.externalId : undefined,
            children: fieldValue.children?.map(child => map(child, level + 1)),
            level,
          }
        }
        result = fieldValues.value[props.value.name]?.map(fieldValue => map(fieldValue))
      }
      return result ?? []
    })

    const valueHierarchical = computed(() =>
      valueOptions.value.some(valueOption => valueOption.children?.length),
    )

    const valueDisplayOptions = computed<Array<EpbSelectOption>>(() => {
      const value = props.value.value

      if (
        !props.allowCustomValue ||
        !value ||
        ArrayUtils.findInHierarchy(value, valueOptions.value, 'value').length
      ) {
        return valueOptions.value
      }

      return [...valueOptions.value, { value, label: value }]
    })

    const isCustomValueTextbox = computed(
      () =>
        props.allowCustomValue &&
        !!props.value.name &&
        !loadingValues.value &&
        !valueOptions.value.length,
    )

    const valueError = computed<string | undefined>(
      () =>
        props.error ||
        (props.allowCustomValue
          ? undefined
          : props.value.name && !loadingValues.value && !valueOptions.value.length
            ? props.messages.value.errors.noValues
            : undefined),
    )

    const filters = computed(() =>
      Object.entries(props.pivots).reduce<typeof props.pivots>((result, [key, pivot]) => {
        const resultItem = {
          ...pivot,
          config: {
            ...pivot.config,
            multiselectDisabled: true,
            mandatory: true,
            values: !pivot.selectedValues.length
              ? pivot.config.values
              : pivot.config.values.filter(value => pivot.selectedValues.includes(value.id)),
          },
          selectedValues: filtersSelectedValues.value[key] ?? [],
        }
        if (!resultItem.selectedValues.length) {
          resultItem.selectedValues = FiltersUtils.getDefaultSelectedValues(resultItem, true)
        }
        return {
          ...result,
          [key]: resultItem,
        }
      }, {}),
    )

    const fieldValuesLoadParams = computed<FieldValuesComposableLoadParams | undefined>(() => {
      let result: FieldValuesComposableLoadParams
      if (props.value.name) {
        const selectedAttribute = props.nameOptions.find(
          option => option.value === props.value.name,
        )
        result = {
          attribute: props.value.name,
          pivots: Object.values(filters.value).map(filter => ({
            name: FiltersUtils.getEncodedId(filter.config),
            values: filter.selectedValues.map(selectedValue =>
              FiltersUtils.encodeValue(filter.config, selectedValue),
            ),
          })),
          isCategory: selectedAttribute?.isCategory ?? false,
        }
      }
      return result
    })

    function onNameInput($event: string | string[]) {
      if (Array.isArray($event)) {
        emit('input', { name: undefined, value: undefined })
        return
      }
      emit('input', { name: $event, value: undefined })
    }

    watch(
      fieldValuesLoadParams,
      async value => {
        if (!value) {
          return
        }
        const paramsKey = getFieldValuesLoadParamsKey(value)
        if (paramsKey === lastLoadedParamsKey) {
          return
        }
        lastLoadedParamsKey = paramsKey
        loadingValues.value = true
        try {
          await loadFieldValues(value)
        } catch (error) {
          emit('error', getErrorMessage(error))
        } finally {
          loadingValues.value = false
        }
      },
      { immediate: true },
    )

    watch(
      () => props.pivots,
      value => {
        Object.entries(filtersSelectedValues.value).forEach(([key, selectedValues]) => {
          filtersSelectedValues.value[key] = selectedValues.filter(selectedValue =>
            value[key]?.config.values.some(value => value.id === selectedValue),
          )
        })
      },
    )

    return {
      baseClass,
      loadingValues,
      filtersSelectedValues,
      valueOptions,
      valueDisplayOptions,
      valueHierarchical,
      isCustomValueTextbox,
      filters,
      valueError,
      onNameInput,
    }
  },
})
